/**
 * Handing a file the app already holds to the person using it.
 *
 * Not a request — the bytes are in memory by the time this is called, so §6's rule about
 * everything going through `@agos/api-client` is untouched. What this exists for is the
 * cleanup: an object URL that is never revoked pins its blob for the life of the tab, and
 * the SEPA batch on `/invoices` is a file somebody may generate a dozen times in an
 * afternoon.
 */

/** Returned so a caller can assert on what was produced without reading the disk. */
export interface DownloadedFile {
  fileName: string
  mediaType: string
  bytes: number
}

/**
 * Save text as a file.
 *
 * The blob carries an explicit charset because the SEPA remittance fields hold Slovak
 * supplier names, and a bank's upload form reading them as Latin-1 turns *Kuracie prsia*
 * into mojibake in a payment reference.
 */
export function downloadTextFile(
  fileName: string,
  text: string,
  mediaType = 'text/plain',
): DownloadedFile {
  const type = mediaType.includes('charset') ? mediaType : `${mediaType};charset=utf-8`
  const blob = new Blob([text], { type })
  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    anchor.rel = 'noopener'
    // Firefox needs the element in the document for a programmatic click to take.
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
  return { fileName, mediaType: type, bytes: new TextEncoder().encode(text).length }
}

/**
 * Save bytes the app already holds as a file.
 *
 * The binary sibling of `downloadTextFile`, added for the database backups on
 * `/settings-backups`: a gzipped `pg_dump` arrives as a `Blob` from the API client and must reach
 * the disk unaltered. Text and bytes are separate functions rather than one with a union
 * parameter, because the charset the text path appends would corrupt a gzip.
 */
export function downloadBlobFile(fileName: string, blob: Blob): DownloadedFile {
  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    anchor.rel = 'noopener'
    // Firefox needs the element in the document for a programmatic click to take.
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
  return { fileName, mediaType: blob.type, bytes: blob.size }
}
